import { Button, Stack } from "@mui/material";
import { createElement, useState } from "react";
import { useTranslation } from "react-i18next";
import FaceIconBrandLarge from "@/theme/icons/FaceIconBrandLarge";
import QuestionTypeWeight from "@/features/Common/components/QuestionTypeWeight";
import QuestionTypeHeight from "@/features/Common/components/QuestionTypeHeight";

interface PreFaceScanFlowProps {
  onComplete: Function;
  onHandlerNextQuestionType: Function;
}

type QuestionTypeName = "Weight" | "Height";

interface QuestionTypesMetadata {
  [key: string]: {
    questionType: QuestionTypeName;
    component: any;
    onChange?: (value: any) => void;
    onCompletedFlow?: () => void;
  };
}

export default function PreFaceScanFlow({
  onComplete,
  onHandlerNextQuestionType,
}: PreFaceScanFlowProps) {
  const stateNames: { [key: string]: QuestionTypeName } = {
    QUESTION_TYPE_WEIGHT: "Weight",
    QUESTION_TYPE_HEIGHT: "Height",
  };

  const { t } = useTranslation();

  const [currentQuestionType, setCurrentQuestionType] =
    useState<QuestionTypeName>(stateNames.QUESTION_TYPE_WEIGHT);

  const [isValidInput, setIsValidInput] = useState(false);
  const [subjectDemographic, setSubjectDemographic] = useState<any>({});

  const questionTypesMapping: QuestionTypesMetadata = {
    Weight: {
      questionType: "Weight",
      component: QuestionTypeWeight,
      onChange: onWeightChange,
    },
    Height: {
      questionType: "Height",
      component: QuestionTypeHeight,
      onChange: onHeightChange,
    },
  };

  function getQuestionTypeComponent() {
    const { component, onChange, onCompletedFlow } =
      questionTypesMapping[currentQuestionType];

    return createElement(component, {
      onChange: onChange,
      onCompletedFlow: onCompletedFlow,
    });
  }

  function onWeightChange(value: any) {
    if (value.isValid) {
      setSubjectDemographic({
        ...subjectDemographic,
        weight: value.data.value,
      });
      setIsValidInput(true);
    }
  }

  function onHeightChange(value: any) {
    if (value.isValid) {
      setSubjectDemographic({
        ...subjectDemographic,
        height: value.data.value,
      });
      setIsValidInput(true);
    }
  }

  const onContinue = () => {
    setIsValidInput(false);
    onHandlerNextQuestionType();

    if (currentQuestionType === stateNames.QUESTION_TYPE_WEIGHT) {
      setCurrentQuestionType(stateNames.QUESTION_TYPE_HEIGHT);
    }

    if (currentQuestionType === stateNames.QUESTION_TYPE_HEIGHT) {
      onComplete(subjectDemographic);
    }
  };

  return (
    <Stack spacing={4} p={2} justifyContent="flex-end" sx={{ height: "100%" }}>
      <FaceIconBrandLarge />

      {getQuestionTypeComponent()}

      <Stack spacing={1} direction="row" sx={{ width: "100%" }}>
        <Button fullWidth disabled={!isValidInput} onClick={onContinue}>
          {t("common.nextButton")}
        </Button>
      </Stack>
    </Stack>
  );
}
